import React from 'react';
import { Link } from 'react-router-dom';
import Header from './HeaderHome';
import Footer from './FooterHome';

const Volunteer = () => {
  // ── Service categories as volunteer opportunities ──
  const serviceCategories = [
    {
      icon: "fas fa-user-friends",
      title: "Care for Senior Citizens",
      desc: "Help provide companionship, assistance with daily tasks, and emotional support to elderly individuals.",
      link: "/care-for-senior-citizens",
    },
    {
      icon: "fas fa-child",
      title: "Child Welfare",
      desc: "Support children through education, mentoring, nutrition programmes, and creative activities.",
      link: "/child-welfare",
    },
    {
      icon: "fas fa-wheelchair",
      title: "Support for Persons with Disabilities",
      desc: "Assist with mobility, rehabilitation, skill development, and advocacy for inclusive opportunities.",
      link: "/disability-support",
    },
    {
      icon: "fas fa-heartbeat",
      title: "Health & Medical Assistance",
      desc: "Volunteer at health camps, blood donation drives, and help spread health awareness.",
      link: "/health-medical",
    },
    {
      icon: "fas fa-graduation-cap",
      title: "Education for Poor Students",
      desc: "Tutor, mentor, or help with scholarships, books, and digital learning for underprivileged children.",
      link: "/education-for-poor",
    },
    {
      icon: "fas fa-leaf",
      title: "Environment & Nature Conservation",
      desc: "Join tree plantation drives, waste management campaigns, and water conservation projects.",
      link: "/environment-conservation",
    },
    {
      icon: "fas fa-hands-helping",
      title: "Community Service",
      desc: "Participate in disaster relief, awareness campaigns, and rural development initiatives.",
      link: "/community-service",
    },
  ];

  // ── General volunteer opportunities ──
  const opportunities = [
    {
      icon: "📷",
      title: "Event & Media Support",
      desc: "Help organise fundraising events, manage social media, create content, or capture stories through photography and video.",
      type: "Flexible / Remote",
    },
    {
      icon: "💼",
      title: "Administrative Support",
      desc: "Assist with data entry, donor communications, coordination, and other back-office tasks to keep our operations running smoothly.",
      type: "Weekdays / Part-time",
    },
  ];

  // ── Why volunteer stats ──
  const stats = [
    { number: "2,400+", label: "Active Volunteers" },
    { number: "15,000+", label: "Hours Contributed" },
    { number: "500+", label: "Events Supported" },
    { number: "98%", label: "Volunteer Satisfaction" },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="volunteer-hero relative text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-hand-peace mr-2"></i> Get Involved
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Volunteer With Us
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Share your time, skills, and compassion to create lasting change
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Why Volunteer With Us?
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Volunteering is more than just giving your time—it's about being part of a movement that transforms
              lives. At Venkatadri Seva Trust, we believe that everyone has something valuable to offer. Whether
              you can spare a few hours a week or want to engage your entire team, your contribution makes a
              tangible difference.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Join our community of passionate volunteers who are bringing hope, dignity, and happiness to the
              underprivileged. From teaching children to caring for senior citizens, there's a role for everyone
              who wants to serve humanity.
            </p>
          </div>
        </div>
      </section>

      {/* ========== SERVICE CATEGORIES (VOLUNTEER ROLES) ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Volunteer by Service Area</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Choose the cause that resonates with you
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {serviceCategories.map((category, idx) => (
              <Link
                key={idx}
                to={category.link}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary hover:scale-105 transform duration-300"
              >
                <div className="service-icon">
                  <i className={`${category.icon} text-3xl text-primary`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800">{category.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{category.desc}</p>
                <span className="inline-block mt-3 text-primary font-medium text-sm">
                  Explore Opportunities →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ========== OTHER VOLUNTEER OPPORTUNITIES ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">More Ways to Help</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Support roles that keep our organisation running smoothly
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {opportunities.map((opp, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{opp.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{opp.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{opp.desc}</p>
                <span className="inline-block mt-3 bg-yellow-100 text-primary text-xs px-3 py-1 rounded-full">
                  {opp.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== STATS ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Volunteer Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== VOLUNTEER REGISTRATION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Register as a Volunteer</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Fill in the form below and our team will get back to you within 48 hours.
              </p>
            </div>

            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Full Name</label>
                  <input
                    type="text"
                    placeholder="Your full name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Email Address</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Phone Number</label>
                <input
                  type="tel"
                  placeholder="+91 98765 43210"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Which area interests you?</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="">Select an opportunity...</option>
                  <option value="education">Education & Mentoring</option>
                  <option value="health">Health & Medical</option>
                  <option value="elder">Elder Care</option>
                  <option value="environment">Environment Conservation</option>
                  <option value="community">Community Service</option>
                  <option value="disability">Disability Support</option>
                  <option value="events">Event & Media Support</option>
                  <option value="admin">Administrative Support</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">When are you available?</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                  <option value="">Select availability...</option>
                  <option value="weekdays">Weekdays</option>
                  <option value="weekends">Weekends</option>
                  <option value="evenings">Evenings</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Why do you want to volunteer?</label>
                <textarea
                  rows="3"
                  placeholder="Tell us about yourself and what motivates you..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Register as Volunteer
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">What Our Volunteers Say</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-white border border-white/20">
              <p className="text-white/90 italic">
                "Volunteering with Venkatadri Seva Trust has been incredibly fulfilling. I've been teaching
                children for over a year now, and seeing their progress brings me so much joy. It's the best
                decision I ever made."
              </p>
              <p className="mt-3 font-semibold text-yellow-200">— Ananya</p>
              <p className="text-yellow-100/70 text-sm">Education Mentor</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-white border border-white/20">
              <p className="text-white/90 italic">
                "I spend my weekends visiting senior citizens. Just listening to their stories and sharing a meal
                makes a huge difference. It's a reminder that small acts of kindness matter."
              </p>
              <p className="mt-3 font-semibold text-yellow-200">— Arjun</p>
              <p className="text-yellow-100/70 text-sm">Elder Care Companion</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CALL TO ACTION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Make a Difference?</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 italic mb-6">
              "Volunteers don't get paid, not because they're worthless, but because they're priceless."
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Join our growing community of passionate volunteers and help us bring hope, dignity, and happiness
              to those who need it most. Your time and skills can change lives.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Register Now
              </a>
              <a
                href="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-phone-alt mr-2"></i> Ask Questions
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Volunteer;